// TfPrimitives — TradeFlow design system primitives used across all 55+ pages
// Reusable building blocks that apply the design tokens consistently.

import { ReactNode } from 'react';

// ─── Card ─────────────────────────────────────────────────────────────────

export function TfCard({ children, className = '', hoverable = false, testId }: { children: ReactNode; className?: string; hoverable?: boolean; testId?: string }) {
  return <div data-testid={testId} className={['tf-card', hoverable ? 'tf-card-hoverable' : '', className].join(' ')}>{children}</div>;
}

export function TfCardFloating({ children, className = '', testId }: { children: ReactNode; className?: string; testId?: string }) {
  return <div data-testid={testId} className={['tf-card-floating', className].join(' ')}>{children}</div>;
}

export function TfCardHero({ children, className = '', testId }: { children: ReactNode; className?: string; testId?: string }) {
  return <div data-testid={testId} className={['tf-card-hero', className].join(' ')}>{children}</div>;
}

export function TfCardList({ children, testId }: { children: ReactNode; testId?: string }) {
  return <div data-testid={testId} className="tf-card-list">{children}</div>;
}

// ─── Button ───────────────────────────────────────────────────────────────

export function TfButton({ children, variant = 'primary', size = 'md', onClick, disabled, fullWidth, className = '', testId }: { children: ReactNode; variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'md' | 'lg'; onClick?: () => void; disabled?: boolean; fullWidth?: boolean; className?: string; testId?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={[
        'tf-btn',
        `tf-btn-${variant}`,
        size === 'lg' ? 'tf-btn-lg' : '',
        fullWidth ? 'tf-w-full' : '',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function TfIconButton({ children, onClick, className = '', testId, label }: { children: ReactNode; onClick?: () => void; className?: string; testId?: string; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className={['tf-btn-icon', className].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Badge / Pill ─────────────────────────────────────────────────────────

export function TfBadge({ children, variant = 'info', testId }: { children: ReactNode; variant?: 'success' | 'warning' | 'danger' | 'info' | 'purple'; testId?: string }) {
  return <span data-testid={testId} className={`tf-badge tf-badge-${variant}`}>{children}</span>;
}

export function TfPill({ children, active = false, onClick, testId }: { children: ReactNode; active?: boolean; onClick?: () => void; testId?: string }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={['tf-pill', active ? 'active' : ''].join(' ')}>
      {children}
    </button>
  );
}

// ─── Input ───────────────────────────────────────────────────────────────

export function TfInput({ value, onChange, placeholder, type = 'text', label, error, testId, leading, trailing }: { value: string; onChange: (v: string) => void; placeholder?: string; type?: string; label?: string; error?: string; testId?: string; leading?: ReactNode; trailing?: ReactNode }) {
  return (
    <div>
      {label && <label className="tf-label block mb-1.5 text-[var(--tf-text-2)]">{label}</label>}
      <div className="relative">
        {leading && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tf-text-3)]">{leading}</div>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={['tf-input', leading ? 'pl-10' : '', trailing ? 'pr-10' : '', error ? 'error' : ''].join(' ')}
        />
        {trailing && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tf-text-3)]">{trailing}</div>}
      </div>
      {error && <p className="mt-1 text-[11px] text-[var(--tf-red)]">{error}</p>}
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────

export function TfSectionHeader({ title, action, testId }: { title: string; action?: ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="tf-section-header">
      <span className="tf-section-title">{title}</span>
      {action}
    </div>
  );
}

// ─── List row ────────────────────────────────────────────────────────────

export function TfListRow({ title, sub, trailing, leading, onClick, href, testId }: { title: string; sub?: string; trailing?: ReactNode; leading?: ReactNode; onClick?: () => void; href?: string; testId?: string }) {
  const Tag: any = href ? 'a' : 'button';
  return (
    <Tag
      data-testid={testId}
      onClick={onClick}
      href={href}
      className="tf-list-row w-full"
      style={{ textAlign: 'left', textDecoration: 'none' }}
    >
      {leading}
      <div className="flex-1 min-w-0">
        <div className="row-title">{title}</div>
        {sub && <div className="row-sub">{sub}</div>}
      </div>
      {trailing}
    </Tag>
  );
}

// ─── Sparkline / chart primitives ────────────────────────────────────────

export function TfSparkline({ points, color = '#007AFF', height = 56, fill = true, testId }: { points: number[]; color?: string; height?: number; fill?: boolean; testId?: string }) {
  if (points.length < 2) return <div className="tf-skeleton" style={{ height }} />;
  const w = 600;
  const h = height;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const dx = w / (points.length - 1);
  const path = points.map((p, i) => {
    const x = i * dx;
    const y = h - ((p - min) / range) * (h - 8) - 4;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const areaPath = `${path} L${w} ${h} L0 ${h} Z`;
  const gradId = `spark-grad-${color.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" data-testid={testId} style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={areaPath} fill={`url(#${gradId})`} />}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - ((points[points.length - 1] - min) / range) * (h - 8) - 4} r="3" fill={color} />
    </svg>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────

export function TfEmpty({ icon = '📭', title, description, action, testId }: { icon?: ReactNode; title: string; description?: string; action?: ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="tf-empty">
      <div className="tf-empty-icon">
        {typeof icon === 'string' ? <span style={{ fontSize: 36 }}>{icon}</span> : icon}
      </div>
      <div>
        <h3 className="tf-h3">{title}</h3>
        {description && <p className="tf-body text-[var(--tf-text-2)] mt-1 max-w-[280px] mx-auto">{description}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Status dot ──────────────────────────────────────────────────────────

export function TfStatusDot({ variant = 'success', pulsing = false, testId }: { variant?: 'success' | 'warning' | 'danger' | 'info'; pulsing?: boolean; testId?: string }) {
  const color = { success: 'var(--tf-green)', warning: '#FF9500', danger: 'var(--tf-red)', info: '#007AFF' }[variant];
  return <span data-testid={testId} className={['tf-status-dot', pulsing ? 'live' : ''].join(' ')} style={{ background: color }} />;
}

// ─── Avatar ──────────────────────────────────────────────────────────────

export function TfAvatar({ name, size = 'md', src, testId }: { name: string; size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; src?: string; testId?: string }) {
  const initial = (name || '?').trim().split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  if (src) {
    return <img src={src} alt={name} data-testid={testId} className={['tf-avatar', `tf-avatar-${size}`].join(' ')} style={{ objectFit: 'cover' }} />;
  }
  return <div data-testid={testId} className={['tf-avatar', `tf-avatar-${size}`].join(' ')}>{initial}</div>;
}

// ─── Progress ───────────────────────────────────────────────────────────

export function TfProgress({ value, max = 100, color, testId }: { value: number; max?: number; color?: string; testId?: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div data-testid={testId} className="tf-progress">
      <div className="tf-progress-fill" style={{ width: `${pct}%`, background: color || '#007AFF' }} />
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────

export function TfSkeleton({ height = 16, width = '100%', radius = 'var(--tf-r-md)', className = '', testId }: { height?: number | string; width?: number | string; radius?: string; className?: string; testId?: string }) {
  return <div data-testid={testId} className={['tf-skeleton', className].join(' ')} style={{ height, width, borderRadius: radius }} />;
}

// ─── Chip row ────────────────────────────────────────────────────────────

export function TfChipRow({ children }: { children: ReactNode }) {
  return <div className="tf-chip-row px-4">{children}</div>;
}

// ─── Search ──────────────────────────────────────────────────────────────

export function TfSearch({ value, onChange, placeholder = 'Search', testId, leading }: { value: string; onChange: (v: string) => void; placeholder?: string; testId?: string; leading?: ReactNode }) {
  return (
    <div data-testid={testId} className="tf-search">
      {leading ?? <svg className="search-icon w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" strokeLinecap="round" /></svg>}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

// ─── Haptic click wrapper ────────────────────────────────────────────────

export function TfHaptic({ children, onClick, className = '', testId }: { children: ReactNode; onClick?: () => void; className?: string; testId?: string }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={['tf-anim-scaleIn', className].join(' ')} style={{ background: 'transparent', border: 0, padding: 0 }}>
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// v32 — Design species from reference photos
// Applied to upgrade existing 55+ pages with the Apple-fintech look
// ══════════════════════════════════════════════════════════════════════════

// ─── Free Reward Card ────────────────────────────────────────────────────
// Used for: "Claim $4.00 of free stocks", "Earn 5 profitable days bonus",
// "Activate platform", "Welcome bonus" — all countdown + CTA patterns.

export function TfRewardCard({
  title,
  description,
  countdown,
  ctaLabel,
  onCta,
  testId,
  className = '',
}: {
  title: string;
  description?: string;
  countdown?: string;
  ctaLabel: string;
  onCta?: () => void;
  testId?: string;
  className?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={['mx-4 mb-3 rounded-[18px] bg-white p-4', className].join(' ')}
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold leading-tight" style={{ color: '#1C1C1E' }}>
            {title}
          </div>
          {description && (
            <div className="mt-1 text-[13px]" style={{ color: '#8E8E93' }}>
              {description}
            </div>
          )}
          {countdown && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="text-[11px] font-medium" style={{ color: '#8E8E93' }}>Expires in:</span>
              <span className="text-[11px] font-semibold tabular-nums" style={{ color: '#1C1C1E' }}>{countdown}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onCta}
          className="flex h-10 items-center gap-1.5 rounded-full bg-[#1C1C1E] px-5 text-[14px] font-semibold text-white active:scale-[0.97] transition-transform"
        >
          {ctaLabel}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7" /><path d="M7 7h10v10" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Section header with optional "View all" link ────────────────────────

export function TfSectionHeaderView({
  title,
  viewAllLabel,
  onViewAll,
  testId,
  className = '',
}: {
  title: string;
  viewAllLabel?: string;
  onViewAll?: () => void;
  testId?: string;
  className?: string;
}) {
  return (
    <div data-testid={testId} className={['flex items-center justify-between px-4 pt-4 pb-2', className].join(' ')}>
      <h3 className="text-[17px] font-semibold" style={{ color: '#1C1C1E' }}>{title}</h3>
      {viewAllLabel && (
        <button onClick={onViewAll} className="flex items-center gap-1 text-[13px] font-medium" style={{ color: '#007AFF' }}>
          {viewAllLabel}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Time range pills (1D 7D 1M 3M 6M 1Y) ────────────────────────────────

export function TfTimeRange({
  value,
  onChange,
  options = ['1D', '7D', '1M', '3M', '6M', '1Y'],
  testId,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  options?: string[];
  testId?: string;
  className?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={['flex items-center justify-center gap-1.5 py-2', className].join(' ')}
    >
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={[
              'h-7 min-w-[36px] rounded-full px-2.5 text-[12px] font-semibold transition-colors',
              active
                ? 'bg-[#1C1C1E] text-white'
                : 'bg-transparent text-[#8E8E93] hover:bg-[#F2F2F7]',
            ].join(' ')}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── Holding row (icon + name + Avg + shares + colored % pill + spark) ───

export function TfHoldingRow({
  icon,
  iconBg,
  name,
  ticker,
  avgPrice,
  shares,
  currentPrice,
  changePct,
  sparkline,
  onClick,
  testId,
}: {
  icon: ReactNode;
  iconBg?: string;
  name: string;
  ticker?: string;
  avgPrice?: string;
  shares?: string;
  currentPrice?: string;
  changePct: number;
  sparkline?: number[];
  onClick?: () => void;
  testId?: string;
}) {
  const positive = changePct >= 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-[#F2F2F7]"
    >
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full text-white"
        style={{ background: iconBg || '#007AFF' }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold" style={{ color: '#1C1C1E' }}>{name}</div>
        {avgPrice && (
          <div className="mt-0.5 text-[12px]" style={{ color: '#8E8E93' }}>
            Avg {avgPrice} · {shares} shares
          </div>
        )}
        {sparkline && sparkline.length > 1 && (
          <div className="mt-1" style={{ width: 80, height: 24 }}>
            <TfSparkline
              points={sparkline}
              color={positive ? '#34C759' : '#FF3B30'}
              height={24}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {currentPrice && (
          <div className="text-[15px] font-semibold tabular-nums" style={{ color: '#1C1C1E' }}>
            {currentPrice}
          </div>
        )}
        <div
          className="rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums"
          style={{
            color: positive ? '#248A3D' : '#B82B22',
            background: positive ? 'var(--tf-green-tint)' : 'var(--tf-red-tint)',
          }}
        >
          {positive ? '+' : ''}{changePct.toFixed(2)}%
        </div>
      </div>
    </button>
  );
}

// ─── Asset detail header (Ethereum-style: title + $ amount + % pill) ────

export function TfAssetHeader({
  title,
  iconBg,
  icon,
  price,
  changePct,
  changeAbs,
  period = 'Today',
  testId,
}: {
  title: string;
  iconBg?: string;
  icon?: ReactNode;
  price: number;
  changePct: number;
  changeAbs?: number;
  period?: string;
  testId?: string;
}) {
  const positive = changePct >= 0;
  return (
    <div data-testid={testId} className="px-4 pt-2">
      <div className="flex items-center gap-2">
        {icon && (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full text-white"
            style={{ background: iconBg || '#007AFF' }}
          >
            {icon}
          </div>
        )}
        <h1 className="text-[20px] font-bold" style={{ color: '#1C1C1E' }}>{title}</h1>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-[34px] font-bold tabular-nums tracking-tight" style={{ color: '#1C1C1E' }}>
          ${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[13px]">
        <span className="font-semibold" style={{ color: positive ? '#34C759' : '#FF3B30' }}>
          {positive ? '+' : ''}{changePct.toFixed(2)}%
        </span>
        {changeAbs !== undefined && (
          <span style={{ color: '#8E8E93' }}>
            {positive ? '+' : ''}${Math.abs(changeAbs).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
        <span className="flex items-center gap-0.5" style={{ color: '#8E8E93' }}>
          {period}
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </div>
    </div>
  );
}

// ─── Sparkline with active marker (ETH-style chart with value pill) ─────

export function TfSparklineMarker({
  points,
  color = '#1C1C1E',
  fillColor = 'rgba(0,122,255,0.12)',
  height = 200,
  markerIndex,
  markerLabel,
  markerPct,
  testId,
}: {
  points: number[];
  color?: string;
  fillColor?: string;
  height?: number;
  markerIndex?: number;
  markerLabel?: string;
  markerPct?: number;
  testId?: string;
}) {
  if (!points || points.length < 2) {
    return <div style={{ height }} className="flex items-center justify-center text-[#8E8E93]">No data</div>;
  }
  const w = 600;
  const h = height;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = w / (points.length - 1);
  // Build smooth bezier
  const coords = points.map((p, i) => ({
    x: i * stepX,
    y: h - ((p - min) / range) * (h - 16) - 8,
  }));
  let path = `M${coords[0].x.toFixed(1)} ${coords[0].y.toFixed(1)}`;
  for (let i = 1; i < coords.length; i++) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const cx1 = prev.x + (curr.x - prev.x) * 0.5;
    const cy1 = prev.y;
    const cx2 = prev.x + (curr.x - prev.x) * 0.5;
    const cy2 = curr.y;
    path += ` C${cx1.toFixed(1)} ${cy1.toFixed(1)} ${cx2.toFixed(1)} ${cy2.toFixed(1)} ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`;
  }
  const fillPath = `${path} L${w} ${h} L0 ${h} Z`;
  const midIndex = markerIndex ?? Math.floor(coords.length * 0.6);
  const marker = coords[midIndex];
  return (
    <div data-testid={testId} className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
        <defs>
          <linearGradient id={`sgrad-${testId || 'x'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill={`url(#sgrad-${testId || 'x'})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {marker && (
          <>
            <line x1={marker.x} y1="0" x2={marker.x} y2={h} stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.3" />
            <circle cx={marker.x} cy={marker.y} r="6" fill={color} />
            <circle cx={marker.x} cy={marker.y} r="3" fill="#FFFFFF" />
          </>
        )}
      </svg>
      {marker && markerLabel && (
        <div
          className="absolute flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold text-white"
          style={{
            background: '#1C1C1E',
            left: `${(marker.x / w) * 100}%`,
            top: `${(marker.y / h) * 100}%`,
            transform: 'translate(-50%, -180%)',
            whiteSpace: 'nowrap',
          }}
        >
          <span className="tabular-nums">{markerLabel}</span>
          {markerPct !== undefined && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums"
              style={{ background: color, color: '#FFFFFF' }}
            >
              {markerPct >= 0 ? '+' : ''}{markerPct.toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Floating search bar (header style) ─────────────────────────────────

export function TfSearchBar({ value, onChange, placeholder = 'Search', testId }: { value: string; onChange: (v: string) => void; placeholder?: string; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="mx-4 mb-3 flex h-9 items-center gap-2 rounded-full px-3"
      style={{ background: 'var(--tf-surface-3, #F0F0F5)' }}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#8E8E93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#8E8E93]"
        style={{ color: '#1C1C1E' }}
      />
    </div>
  );
}

// ─── Full screen number-pad (Buy / Sell modal) ─────────────────────────

export function TfNumberPad({
  value,
  onChange,
  onCancel,
  onConfirm,
  symbol = '',
  orderType = 'Market',
  productType = 'CNC',
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  symbol?: string;
  orderType?: string;
  productType?: string;
  testId?: string;
}) {
  const press = (k: string) => {
    if (k === '⌫') onChange(value.slice(0, -1));
    else if (k === '.') {
      if (!value.includes('.')) onChange(value + '.');
    } else onChange(value + k);
  };
  return (
    <div data-testid={testId} className="rounded-t-[20px] bg-white p-4" style={{ boxShadow: '0 -8px 32px rgba(0,0,0,0.08)' }}>
      <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-[#E5E5EA]" />
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-full bg-[#1C1C1E] px-2.5 py-1 text-[11px] font-semibold text-white">NRML order</span>
        <span className="rounded-full bg-[#F2F2F7] px-2.5 py-1 text-[11px] font-medium" style={{ color: '#8E8E93' }}>GTT</span>
        <span className="rounded-full bg-[#F2F2F7] px-2.5 py-1 text-[11px] font-medium" style={{ color: '#8E8E93' }}>SIP</span>
      </div>
      <div className="mb-4 flex items-baseline justify-between">
        <div className="text-[15px] font-medium" style={{ color: '#8E8E93' }}>
          {symbol ? `How many shares of ${symbol} do you want to buy?` : 'Enter amount'}
        </div>
        <div className="text-[40px] font-bold tabular-nums" style={{ color: '#1C1C1E' }}>{value || '0'}</div>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-[10px] border border-[#E5E5EA] p-2.5">
          <div className="text-[11px]" style={{ color: '#8E8E93' }}>Order Type</div>
          <div className="text-[14px] font-medium" style={{ color: '#1C1C1E' }}>{orderType}</div>
        </div>
        <div className="rounded-[10px] border border-[#E5E5EA] p-2.5">
          <div className="text-[11px]" style={{ color: '#8E8E93' }}>Product Type</div>
          <div className="text-[14px] font-medium" style={{ color: '#1C1C1E' }}>{productType}</div>
        </div>
      </div>
      <div className="mb-4 space-y-1.5">
        <div className="flex justify-between text-[13px]">
          <span style={{ color: '#8E8E93' }}>{symbol || 'Asset'} price</span>
          <span className="tabular-nums" style={{ color: '#1C1C1E' }}>000.00</span>
        </div>
        <div className="flex justify-between text-[13px]">
          <span style={{ color: '#8E8E93' }}>Estimated cost</span>
          <span className="tabular-nums" style={{ color: '#1C1C1E' }}>000.00</span>
        </div>
        <div className="flex justify-between text-[13px]">
          <span style={{ color: '#8E8E93' }}>Funds available</span>
          <span className="tabular-nums" style={{ color: '#1C1C1E' }}>000.00</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => press(k)}
            className="flex h-12 items-center justify-center rounded-[10px] bg-[#F2F2F7] text-[20px] font-medium active:bg-[#E5E5EA]"
            style={{ color: '#1C1C1E' }}
          >
            {k}
          </button>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-12 flex-1 rounded-full bg-[#F2F2F7] text-[15px] font-semibold active:scale-[0.98]"
          style={{ color: '#1C1C1E' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="h-12 flex-1 rounded-full bg-[#007AFF] text-[15px] font-semibold text-white active:scale-[0.98]"
        >
          Buy
        </button>
      </div>
    </div>
  );
}

// ─── Confirm reward celebration ────────────────────────────────────────

export function TfConfirmReward({
  icon,
  title,
  description,
  onConfirm,
  onShare,
  onDownload,
  testId,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onConfirm?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="rounded-[20px] bg-white p-6 text-center" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.10)' }}>
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F2F2F7]">
        {icon}
      </div>
      <h2 className="text-[20px] font-bold" style={{ color: '#1C1C1E' }}>{title}</h2>
      <p className="mt-1.5 text-[14px]" style={{ color: '#8E8E93' }}>{description}</p>
      <button
        type="button"
        onClick={onConfirm}
        className="mt-5 h-12 w-full rounded-full bg-[#1C1C1E] text-[15px] font-semibold text-white active:scale-[0.98]"
      >
        Confirm reward
      </button>
      <div className="mt-3 flex justify-center gap-6">
        {onShare && (
          <button className="flex flex-col items-center gap-1">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#1C1C1E' }}><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
            <span className="text-[10px]" style={{ color: '#8E8E93' }}>Share</span>
          </button>
        )}
        {onDownload && (
          <button className="flex flex-col items-center gap-1">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#1C1C1E' }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            <span className="text-[10px]" style={{ color: '#8E8E93' }}>Download</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Stat row (3-up: Average Profit / Loss / Strike Rate) ──────────────

export function TfStatRow({
  stats,
  testId,
}: {
  stats: { label: string; value: string; color?: string }[];
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="grid grid-cols-3 gap-3 px-4">
      {stats.map((s, i) => (
        <div key={i} className="text-center">
          <div className="text-[11px] font-medium" style={{ color: '#8E8E93' }}>{s.label}</div>
          <div className="mt-0.5 text-[18px] font-bold tabular-nums" style={{ color: s.color || '#1C1C1E' }}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}
